import React from 'react';
import { Award, TrendingUp, Info } from 'lucide-react';
import { getPerformanceLabel, hasPerformanceScore } from '../client/VendorPerformanceBadge';

const PlatformPerformanceCard = ({ performance }) => {
  if (!performance) {
    return null;
  }

  const score = Number(performance.score || 0);
  const bookings = Number(performance.platform_bookings_count || 0);
  const label = performance.label || getPerformanceLabel(score);
  const visible = performance.visible_to_clients ?? hasPerformanceScore(score, bookings);

  return (
    <div className="bg-white rounded-lg border border-emerald-100 p-4 sm:p-6 mb-6">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
        <div className="flex items-start gap-4">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-emerald-50 border border-emerald-200">
            <span className="text-2xl font-bold text-emerald-800 tabular-nums">
              {score > 0 ? score.toFixed(0) : '—'}
            </span>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Award className="w-5 h-5 text-emerald-600" />
              <h2 className="text-lg font-semibold text-gray-900">Platform performance score</h2>
            </div>
            <p className="text-sm text-gray-600 max-w-xl">
              Based on your last 90 days of platform bookings. Clients see this as{' '}
              <strong>{label || 'your score'}</strong> when you sort in Best Match.
            </p>
            {visible ? (
              <p className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full">
                <TrendingUp className="w-3 h-3" />
                Visible on your public profile
              </p>
            ) : (
              <p className="mt-2 text-xs text-gray-500">
                Complete platform bookings to earn a public performance badge.
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 lg:min-w-[280px]">
          <div className="rounded-lg bg-gray-50 p-3">
            <p className="text-xs text-gray-500">Completion</p>
            <p className="text-lg font-bold text-gray-900 tabular-nums">
              {Number(performance.completion_rate || 0).toFixed(0)}%
            </p>
          </div>
          <div className="rounded-lg bg-gray-50 p-3">
            <p className="text-xs text-gray-500">Platform bookings</p>
            <p className="text-lg font-bold text-gray-900 tabular-nums">{bookings}</p>
          </div>
          <div className="rounded-lg bg-gray-50 p-3 col-span-2 sm:col-span-1">
            <p className="text-xs text-gray-500">Avg rating</p>
            <p className="text-lg font-bold text-gray-900 tabular-nums">
              {Number(performance.average_rating || 0).toFixed(1)}
            </p>
          </div>
        </div>
      </div>

      {performance.factors?.length > 0 && (
        <div className="mt-5 pt-5 border-t border-gray-100">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
            How your score is calculated
          </p>
          <div className="flex flex-wrap gap-2">
            {performance.factors.map((factor) => (
              <span
                key={factor.name}
                className="text-xs bg-gray-100 text-gray-700 px-2.5 py-1 rounded-full"
              >
                {factor.name} ({factor.weight})
              </span>
            ))}
          </div>
        </div>
      )}

      {performance.tips?.length > 0 && (
        <div className="mt-4 rounded-lg bg-blue-50 border border-blue-100 p-4">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
            <ul className="text-sm text-blue-900 space-y-1 list-disc list-inside">
              {performance.tips.map((tip) => (
                <li key={tip}>{tip}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformPerformanceCard;
